import {
  File,
  Person,
  AnalysisFile,
  AnalysisClips,
  HlReel,
  Shot,
} from '../types';
import { isEmpty, getFileImg, getSampleSelected } from '../utils';

import { getAnalysisStatus } from './api/events';
import { getFaceById } from './api/faces';
import {
  getClipFiles,
  getClipResults,
  getAttendeeFacePhotos,
} from './api/files';
import { getTemplateData } from './api/template';

const getPersonClips = (
  allClips: File[],
  faceId: string,
  allFiles?: File[],
) => {
  // minimal clip score to be considered 'good'
  const MIN_CLIP_SCORE = 5000;

  let clips: File[] = [];
  let lowScoreClips: File[] = [];

  allClips.forEach((clip) => {
    // reduce clip duration by 0.3 sec to create a buffer and make sure it fully fits the wildcard
    if (clip.segment?.durationMilliseconds)
      clip.segment.durationMilliseconds -= 300;

    // check if this clip has a person with a matching faceId
    let scoreObj = (clip.personsScore || []).find(
      (personScore) => personScore.faceId === faceId,
    );

    const isUGCUpload = !!allFiles?.find(
      (f) => f.elementId === clip.parentElementId,
    )?.parentElementId;

    const score = scoreObj?.score ?? 0;

    if (score >= MIN_CLIP_SCORE || isUGCUpload) clips.push({ ...clip, score });
    else lowScoreClips.push({ ...clip, score, isLowScore: true });
  });

  // sort the clips by the score in descending order
  clips.sort((a, b) => b.score - a.score);
  lowScoreClips.sort((a, b) => b.score - a.score);

  return { clips, lowScoreClips, allClips: clips.concat(lowScoreClips) };
};

export const getAnalysisData = async (props: {
  eventId: string;
  isPublic?: boolean;
}): Promise<{
  peopleCount: number;
  hlReel: AnalysisFile | null;
  isHlReelAnalysed: boolean;
  clips: AnalysisClips | null;
  clipsCount: number;
  isClipsAnalysed: boolean;
  isAnalysed: boolean;
  error: string | null;
}> => {
  const { eventId, isPublic } = props;

  const { clips, template, peopleCount, highlightReelCount, clipsCount } =
    await getAnalysisStatus({ eventId, isPublic });

  const baseResponse = {
    peopleCount,
    hlReel: template,
    isHlReelAnalysed: false,
    clips: null,
    clipsCount,
    isClipsAnalysed: false,
    isAnalysed: false,
    error: null,
  };

  if (peopleCount === 0) return { ...baseResponse, error: 'no-people' };
  if (highlightReelCount === 0) return { ...baseResponse, error: 'no-hl-reel' };
  if (clipsCount === 0) return { ...baseResponse, error: 'no-clips' };

  const analysisClips: AnalysisClips = {
    newClips: [],
    convertingClips: [],
    analysingClips: [],
    finishedClips: [],
    errorClips: [],
  };

  clips.forEach((clip: AnalysisFile) => {
    switch (clip.status) {
      case 'pending':
        analysisClips.newClips.push(clip);
        break;
      case 'converting':
        analysisClips.convertingClips.push(clip);
        break;
      case 'preparing':
      case 'analysing':
        analysisClips.analysingClips.push(clip);
        break;
      case 'completed':
        analysisClips.finishedClips.push(clip);
        break;
      case 'failed':
        analysisClips.errorClips.push(clip);
        break;
      default:
        break;
    }
  });

  const isClipsAnalysed =
    isEmpty(analysisClips.convertingClips) &&
    isEmpty(analysisClips.analysingClips);
  const isHlReelAnalysed = template.status === 'completed';

  return {
    ...baseResponse,
    isHlReelAnalysed,
    clips: analysisClips,
    isClipsAnalysed,
    isAnalysed: isClipsAnalysed && isHlReelAnalysed,
  };
};

export const getReviewData = async (props: {
  eventId: string;
  isPublic?: boolean;
  isSample?: boolean;
}): Promise<{
  people: Person[];
  hlReel: HlReel | null;
  requiredShots: Shot[];
  clips: AnalysisClips | null;
  isAnalysed: boolean;
  error: string | null;
}> => {
  const { eventId, isPublic, isSample } = props;

  const { isHlReelAnalysed, clips, isAnalysed, error } = await getAnalysisData({
    eventId,
    isPublic,
  });

  if (error)
    return {
      clips: null,
      hlReel: null,
      requiredShots: [],
      people: [],
      isAnalysed: false,
      error,
    };

  // HIGHLIGHT REEL
  let hlReel: HlReel | null = null;
  let requiredShots: Shot[] = [];

  if (isHlReelAnalysed) {
    const templateData = await getTemplateData(eventId);
    hlReel = templateData.hlReel;
    requiredShots = templateData.requiredShots;
  }

  // PEOPLE
  const { results } = await getClipResults(eventId, isPublic);

  let people: Person[] = [];

  // sample project – filter out non-selected assets
  if (isSample) {
    const sampleProjectData = JSON.parse(
      localStorage.getItem('sampleProjectData') ?? '{}',
    );

    const selectedPeople = getSampleSelected(sampleProjectData.people).map(
      (person: Person) => person.face.elementId,
    );
    const sampleClipsIds = sampleProjectData.clips
      .filter((clip: File) => clip.isSelected && clip.isAnalysed)
      .map((clip: File) => clip.elementId);

    results.forEach((group: Person) => {
      if (selectedPeople.includes(group.face.elementId)) {
        people.push({
          ...group,
          clips: group.clips?.filter((clip) =>
            sampleClipsIds.includes(clip.parentElementId),
          ),
        });
      }
    });
  }

  // usual event – use all results
  else people = [...results];

  const allFiles: File[] = await getClipFiles(eventId, isPublic);

  people.forEach((result) => {
    result.img = getFileImg(result.files?.[0]);

    const { clips, lowScoreClips, allClips } = getPersonClips(
      result.clips ?? [],
      result.face.elementId,
      allFiles,
    );

    result.clips = clips;
    result.lowScoreClips = lowScoreClips;
    result.allClips = allClips;
  });

  return { people, hlReel, requiredShots, clips, isAnalysed, error: null };
};

export const getAttendeeReviewData = async (faceId: string) => {
  let face = await getFaceById(faceId, true);

  if (!face) return { person: null, eventId: null };

  const photos = await getAttendeeFacePhotos(face.eventId, faceId);

  let { results } = await getClipResults(face.eventId, true);

  const allClips =
    results.find((person: Person) => person.face.elementId === faceId)?.clips ??
    [];

  const { clips, lowScoreClips } = getPersonClips(allClips, face.elementId);

  return {
    person: { face, img: getFileImg(photos[0]), clips, lowScoreClips },
    eventId: face.eventId,
  };
};
